import { isAuthError } from "@supabase/supabase-js";
import { useEffect, useMemo, useRef, useState } from "react";
import toast from "react-hot-toast";
import { useLocation, useNavigate } from "react-router-dom";

import { AuthMachineStatus } from "../../../constants";
import { isWelcomeToken, parseAuthLinkToken } from "../../../hooks/auth/authLinkToken";
import { useAuthV2 } from "../../../hooks/auth/useAuthV2";
import { authOperations } from "../../../store/authMachine";
import { getPostAuthRedirect } from "../../../utils";
import { validatePassword } from "../../../utils/authValidation";
import { Loading } from "../../ui";
import { AuthLayout } from "../AuthLayout";
import { AuthLinkConfirm } from "../AuthLinkConfirm";
import { AuthUpdateView } from "./AuthUpdateView";

interface FormErrors {
  password?: string;
  general?: string;
}

export const AuthUpdate = () => {
  const [password, setPassword] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [errors, setErrors] = useState<FormErrors>({});

  const location = useLocation();
  const navigate = useNavigate();
  const { user, authStatus, isPasswordResetPending, updatePassword } = useAuthV2();

  const token = useMemo(() => parseAuthLinkToken(location), [location.search, location.hash]);
  const variant = token && isWelcomeToken(token) ? "welcome" : "password-reset";

  // A signed-in user landing here (invite/reset link already exchanged) enters the
  // machine's reset state; the machine's entry effect owns the server-side flag write.
  // hasEnteredResetPendingRef guards against re-triggering on the transient
  // "authenticated" the machine cycles back through right after a successful
  // password update (PASSWORD_UPDATED lands "authenticated" on its way to /cases) —
  // without it, that transient would look identical to a fresh landing and
  // re-write the flag, bouncing the user right back to this form.
  const hasEnteredResetPendingRef = useRef(false);
  useEffect(() => {
    if (isPasswordResetPending) {
      hasEnteredResetPendingRef.current = true;
      return;
    }
    if (authStatus === AuthMachineStatus.AUTHENTICATED && !hasEnteredResetPendingRef.current) {
      authOperations.enterPasswordReset();
    }
  }, [authStatus, isPasswordResetPending]);

  const validateForm = (): boolean => {
    const newErrors: FormErrors = {};

    const passwordError = validatePassword(password);
    if (passwordError) newErrors.password = passwordError;

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleUpdatePassword = async (e: React.FormEvent) => {
    e.preventDefault();
    setErrors({});

    if (!validateForm()) {
      return;
    }

    setIsLoading(true);

    try {
      await updatePassword(password);
      toast.success("Password updated successfully!");
      const { redirectPath } = getPostAuthRedirect({ location });
      navigate(redirectPath, { replace: true });
    } catch (error) {
      if (isAuthError(error)) {
        setErrors({ general: error.message });
      } else {
        setErrors({ general: "Password update failed. Please try again or contact support!" });
      }
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <AuthLinkConfirm variant={variant}>
      {!user || !isPasswordResetPending ? (
        <Loading />
      ) : (
        <AuthLayout>
          <AuthUpdateView
            email={user.email || ""}
            password={password}
            isLoading={isLoading}
            errors={errors}
            onPasswordChange={setPassword}
            onSubmit={handleUpdatePassword}
          />
        </AuthLayout>
      )}
    </AuthLinkConfirm>
  );
};
